import React, { useState, useContext } from "react";

import { startOfDay } from "date-fns";
import useSWR from "swr";

import {
  Menu,
  BottomBar,
  Seo,
  ContentContainer,
  Status,
  Footer,
  Icon,
} from "~components";
import { BaseContext } from "~context";
import { sdk } from "~lib";

import { Layout } from "~components/common/Layout";
import { SubHeader } from "~components/events";
import { OrderByDirection } from "~graphql/sdk";
import { useRouter } from "next/router";
import { Container, Flex, Link, Text, LegacyText } from "flicket-ui";
import { TourTile } from "~components/tours/tourTile";
import { today } from "~lib/helpers/dates";
import { useQuery } from "~hooks/useQuery";
import { MembershipsDocument } from "~graphql/typed-document-nodes";
import { useTranslation } from "react-i18next";
import { useAccount } from "~hooks";

const getEvents = async (
  _: string,
  orgId: string,
  search: string,
  seasonId?: string,
  isAdmin?: boolean
) => {
  const where = {
    startDate: today,
    ...(search && { q: search }),
    seasonId,
    // Get all events if admin
    isActive: isAdmin ? null : true,
  };

  return sdk({ orgId }).events({
    where,
    orderBy: {
      startDate: OrderByDirection.Asc,
    },
  });
};

const swrConfig = {
  shouldRetryOnError: false,
};

const TourEventsAndMemberships = () => {
  const { organization } = useContext(BaseContext);
  const [showSearch, setShowSearch] = useState(false);
  const [search, setSearch] = useState<string>(undefined);
  const { isAdmin, userSessionChecked } = useAccount();
  const router = useRouter();
  const tourId = router?.query?.tourId;
  const { t } = useTranslation();

  const { data: events, error: eventsError } = useSWR(
    userSessionChecked && organization?.id
      ? ["events", organization?.id, search, tourId, isAdmin]
      : null,
    getEvents,
    swrConfig
  );

  const {
    data: membershipsData,
    error: membershipsError,
    isLoading: isMembershipLoading,
  } = useQuery(userSessionChecked && MembershipsDocument, {
    where: {
      q: search,
      seasonId: tourId as string,
      // Get all memberships if admin
      isActive: isAdmin ? null : true,
    },
  });

  const memberships = membershipsData?.memberships?.edges.map(
    (membership) => membership.node
  );

  const toggleSearch = () => setShowSearch(!showSearch);

  return (
    <>
      <Seo title="Events overview" description="Events overview page for" />
      <Menu>
        <SubHeader
          showSearch={showSearch}
          setSearch={setSearch}
          toggleSearch={toggleSearch}
          hasBackground={!!organization?.branding?.background}
        />
      </Menu>

      <Layout layoutType="main">
        <ContentContainer variant="content">
          <Flex flexDir="column" flex="1">
            <Flex
              justifyContent="space-between"
              alignItems="center"
              width="100%"
              maxWidth="1248px"
              mx={"auto" as any}
              pl={4}
              mt={4}
              d={{ _: "none !important", md: "block !important" }}
            >
              <Link to={"/"}>
                <Flex alignItems="center" opacity="inherit">
                  <Icon icon="chevron-left" fontSize={5} color="white" />
                  {
                    <LegacyText
                      color="white"
                      fontWeight="extraBold"
                      fontSize={3}
                      lineHeight="medium"
                      ml="3/4"
                    >
                      All tours
                    </LegacyText>
                  }
                </Flex>
              </Link>
            </Flex>
            <Container
              innerProps={{ minHeight: { _: 0, md: "320px" } }}
              mb={10}
              mt={2}
            >
              <Flex flexWrap="wrap" m={"-12px" as any} pb="6/4">
                <Status
                  noResult={!events && !memberships}
                  noResultText={`No events or ${t(
                    "membership_name_plural"
                  )} on sale now, please check back soon.`}
                  error={eventsError || membershipsError}
                  loading={(!eventsError && !events) || isMembershipLoading}
                >
                  {!!memberships &&
                    memberships.map(
                      ({ id, name, startDate, thumbnail, venue }) => (
                        <TourTile
                          key={id}
                          title={name}
                          id={id}
                          startDate={startDate}
                          thumbnail={thumbnail}
                          venue={venue}
                          type="membership"
                        />
                      )
                    )}
                  {!!events &&
                    events?.events?.edges.map(
                      ({
                        node: {
                          title,
                          id,
                          startDate,
                          thumbnail,
                          venue,
                          hasPublicRelease,
                          isWaitlistAvailable,
                        },
                      }) => (
                        <TourTile
                          key={id}
                          title={title}
                          id={id}
                          startDate={startDate}
                          thumbnail={thumbnail}
                          venue={venue}
                          hasPublicRelease={hasPublicRelease}
                          isWaitlistAvailable={isWaitlistAvailable}
                          type="event"
                        />
                      )
                    )}
                </Status>
              </Flex>
            </Container>
          </Flex>
          <Footer />
        </ContentContainer>
      </Layout>
      <BottomBar />
    </>
  );
};

export default TourEventsAndMemberships;
